import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useMySocialConnectionsQuery,
  usePublishClipToYouTubeMutation,
} from '@/generated/graphql';
import { CREATE_MY_SCHEDULED_PUBLISH } from '@/queries/scheduledPublishing';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  extractPublishErrorCode,
  mapPublishError,
} from '@/lib/publishing/mapPublishError';
import {
  datetimeLocalValueToIso,
  viewerTimeZone,
} from '@/lib/publishing/calendarMonth';
import { isScheduledAtTooSoon } from '@/lib/publishing/isScheduledAtTooSoon';
import { minScheduleDatetimeLocal } from '@/lib/publishing/minScheduleDatetimeLocal';
import {
  isYouTubePublishableConnection,
} from '@/lib/publishing/resolvePublishEligibility';
import type { PublishDialogTarget, PublishConnection } from '@/lib/publishing/types';
import { PublishWhenControl, type PublishWhen } from '@/components/publishing/PublishWhenControl';

const TITLE_LIMIT = 100;
const DESCRIPTION_LIMIT = 5000;

type PrivacyStatus = 'public' | 'unlisted' | 'private';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clipId: string;
  clipTitle: string;
  initialWhen?: PublishWhen;
  initialDatetimeLocal?: string;
  onPublished?: (target: PublishDialogTarget) => void;
}

function defaultTitle(clipTitle: string): string {
  return clipTitle.trim().slice(0, TITLE_LIMIT);
}

/** User confirmation for direct YouTube publishing. The backend remains the
 * authority for ownership, connection scope, metadata, and media validation. */
export const PublishToYouTubeDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  clipId,
  clipTitle,
  initialWhen,
  initialDatetimeLocal,
  onPublished,
}) => {
  const { t } = useTranslation();
  const [connectionId, setConnectionId] = useState('');
  const [title, setTitle] = useState(() => defaultTitle(clipTitle));
  const [description, setDescription] = useState('');
  const [privacyStatus, setPrivacyStatus] = useState<PrivacyStatus>('public');
  const [madeForKids, setMadeForKids] = useState(false);
  const [when, setWhen] = useState<PublishWhen>('now');
  const [datetimeLocal, setDatetimeLocal] = useState('');
  const [whenError, setWhenError] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryingConnections, setRetryingConnections] = useState(false);
  const targetVersionRef = useRef(0);
  const currentTargetRef = useRef({ open, clipId });
  const mountedRef = useRef(false);
  currentTargetRef.current = { open, clipId };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const {
    data,
    error: connectionsError,
    loading: loadingConnections,
    refetch: refetchConnections,
  } = useMySocialConnectionsQuery({ skip: !open });
  const [publish, { loading }] = usePublishClipToYouTubeMutation();
  const [createScheduled, { loading: scheduling }] = useMutation(CREATE_MY_SCHEDULED_PUBLISH);
  const timeZone = viewerTimeZone();
  const minDatetimeLocal = minScheduleDatetimeLocal(new Date(), timeZone);
  const submitting = loading || scheduling;

  const youtubeConnections = useMemo(
    () => (data?.mySocialConnections ?? []).filter((connection) => connection.platform === 'youtube'),
    [data],
  );
  const eligibleConnections = useMemo(
    () => youtubeConnections.filter((connection) => (
      isYouTubePublishableConnection(connection as PublishConnection)
    )),
    [youtubeConnections],
  );
  const selectedConnectionId = eligibleConnections.some(({ id }) => id === connectionId)
    ? connectionId
    : eligibleConnections[0]?.id ?? '';

  useEffect(() => {
    targetVersionRef.current += 1;
    setConnectionId('');
    setTitle(defaultTitle(clipTitle));
    setDescription('');
    setPrivacyStatus('public');
    setMadeForKids(false);
    setWhen(initialWhen ?? 'now');
    setDatetimeLocal(initialDatetimeLocal ?? '');
    setWhenError(null);
    setErrorMessage(null);
    setRetryingConnections(false);
  }, [open, clipId, clipTitle, initialWhen, initialDatetimeLocal]);

  const close = () => {
    targetVersionRef.current += 1;
    onOpenChange(false);
  };

  const goToAccountSettings = () => {
    close();
    window.dispatchEvent(new CustomEvent('autoclipper:open-account-settings', {
      detail: { section: 'social', platform: 'youtube' },
    }));
  };

  const retryConnections = async () => {
    if (retryingConnections) return;
    setRetryingConnections(true);
    try {
      await refetchConnections();
    } catch {
      // Apollo retains the safe localized error state. Provider/network
      // details are deliberately neither logged nor rendered here.
    } finally {
      setRetryingConnections(false);
    }
  };

  const titleIsValid = title.trim().length > 0 && title.length <= TITLE_LIMIT;
  const descriptionIsValid = description.length <= DESCRIPTION_LIMIT;
  const formIsValid = !!selectedConnectionId
    && titleIsValid
    && descriptionIsValid
    && (when === 'now' || !!datetimeLocal);

  const handlePublish = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formIsValid || submitting) return;

    const submittedClipId = clipId;
    const submittedVersion = targetVersionRef.current;
    const submittedTarget: PublishDialogTarget = {
      clipId: submittedClipId,
      platform: 'youtube',
    };
    const isCurrentSubmission = () => (
      mountedRef.current
      && submittedVersion === targetVersionRef.current
      && currentTargetRef.current.open
      && currentTargetRef.current.clipId === submittedClipId
    );

    try {
      if (when === 'later') {
        const scheduledAt = datetimeLocalValueToIso(datetimeLocal, timeZone);
        if (!scheduledAt || isScheduledAtTooSoon(scheduledAt, new Date())) {
          setWhenError(t('publishing.schedule.tooSoon'));
          return;
        }
        await createScheduled({
          variables: {
            input: {
              clipId: submittedClipId,
              socialConnectionId: selectedConnectionId,
              platform: 'youtube',
              scheduledAt,
              caption: null,
              title: title.trim(),
              description: description || null,
              privacyStatus,
              madeForKids,
            },
          },
        });
        if (!isCurrentSubmission()) return;
        toast.success(t('publishing.schedule.saved'));
        close();
        return;
      }

      const result = await publish({
        variables: {
          clipId: submittedClipId,
          socialConnectionId: selectedConnectionId,
          title: title.trim(),
          description: description || null,
          privacyStatus,
          madeForKids,
        },
      });

      const record = result.data?.publishClipToYouTube;
      const accepted = !!record && record.status !== 'failed';
      const submissionWasCurrent = isCurrentSubmission();
      if (accepted) onPublished?.(submittedTarget);
      if (!submissionWasCurrent) return;

      if (!record || record.status === 'failed') {
        setErrorMessage(t(mapPublishError(record?.fail_reason)));
        return;
      }

      toast.success(t('publishing.youtube.success'));
      close();
    } catch (error) {
      if (!isCurrentSubmission()) return;

      setErrorMessage(t(mapPublishError(extractPublishErrorCode(error))));
    }
  };

  const renderBody = () => {
    if (loadingConnections && !connectionsError) {
      return (
        <div className="flex min-h-24 items-center justify-center text-sm text-muted-foreground">
          <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
          {t('publishing.youtube.loadingAccounts')}
        </div>
      );
    }

    if (connectionsError) {
      return (
        <div
          className="space-y-4 rounded-lg border border-destructive/30 bg-destructive/5 p-4"
          data-testid="youtube-account-load-error"
        >
          <p className="text-sm text-muted-foreground">
            {t('publishing.youtube.accountLoadError')}
          </p>
          <Button
            type="button"
            variant="outline"
            disabled={retryingConnections}
            onClick={() => { void retryConnections(); }}
          >
            {retryingConnections && (
              <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
            )}
            {retryingConnections
              ? t('publishing.youtube.retryingAccounts')
              : t('publishing.youtube.retryAccounts')}
          </Button>
        </div>
      );
    }

    if (youtubeConnections.length === 0) {
      return (
        <div className="space-y-4 rounded-lg border border-dashed p-4" data-testid="youtube-no-connection">
          <p className="text-sm text-muted-foreground">{t('publishing.youtube.noConnectionBody')}</p>
          <Button type="button" onClick={goToAccountSettings}>
            {t('publishing.youtube.accountSettingsAction')}
          </Button>
        </div>
      );
    }

    if (eligibleConnections.length === 0) {
      return (
        <div className="space-y-4 rounded-lg border border-dashed p-4" data-testid="youtube-reconnect">
          <p className="text-sm text-muted-foreground">{t('publishing.youtube.reconnectBody')}</p>
          <Button type="button" onClick={goToAccountSettings}>
            {t('publishing.youtube.accountSettingsAction')}
          </Button>
        </div>
      );
    }

    return (
      <form className="space-y-5" onSubmit={handlePublish}>
        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="youtube-publish-account">
            {t('publishing.youtube.accountLabel')}
          </label>
          <Select value={selectedConnectionId} onValueChange={setConnectionId}>
            <SelectTrigger
              id="youtube-publish-account"
              aria-label={t('publishing.youtube.accountLabel')}
              data-testid="youtube-account-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {eligibleConnections.map((connection) => (
                <SelectItem key={connection.id} value={connection.id}>
                  {connection.display_name || connection.username}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <label className="text-sm font-medium" htmlFor="youtube-publish-title">
              {t('publishing.youtube.titleLabel')}
            </label>
            <span
              className="tabular-nums text-xs text-muted-foreground"
              data-testid="youtube-title-count"
            >
              {t('publishing.youtube.characterCount', { current: title.length, max: TITLE_LIMIT })}
            </span>
          </div>
          <Input
            id="youtube-publish-title"
            aria-describedby="youtube-publish-title-help"
            aria-invalid={!titleIsValid}
            aria-required="true"
            maxLength={TITLE_LIMIT}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <p id="youtube-publish-title-help" className="text-xs text-muted-foreground">
            {title.trim().length === 0
              ? t('publishing.youtube.titleRequired')
              : t('publishing.youtube.titleHelp')}
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <label className="text-sm font-medium" htmlFor="youtube-publish-description">
              {t('publishing.youtube.descriptionLabel')}
            </label>
            <span
              className="tabular-nums text-xs text-muted-foreground"
              data-testid="youtube-description-count"
            >
              {t('publishing.youtube.characterCount', {
                current: description.length,
                max: DESCRIPTION_LIMIT,
              })}
            </span>
          </div>
          <Textarea
            id="youtube-publish-description"
            aria-describedby="youtube-publish-description-help"
            maxLength={DESCRIPTION_LIMIT}
            rows={4}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <p id="youtube-publish-description-help" className="text-xs text-muted-foreground">
            {t('publishing.youtube.descriptionHelp')}
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">{t('publishing.youtube.visibilityLabel')}</legend>
            <RadioGroup
              aria-label={t('publishing.youtube.visibilityLabel')}
              value={privacyStatus}
              onValueChange={(value) => setPrivacyStatus(value as PrivacyStatus)}
            >
              {(['public', 'unlisted', 'private'] as const).map((value) => (
                <div key={value} className="flex items-center gap-2">
                  <RadioGroupItem id={`youtube-visibility-${value}`} value={value} />
                  <label className="text-sm" htmlFor={`youtube-visibility-${value}`}>
                    {t(`publishing.youtube.visibility.${value}`)}
                  </label>
                </div>
              ))}
            </RadioGroup>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">{t('publishing.youtube.audienceLabel')}</legend>
            <RadioGroup
              aria-label={t('publishing.youtube.audienceLabel')}
              value={madeForKids ? 'made-for-kids' : 'not-made-for-kids'}
              onValueChange={(value) => setMadeForKids(value === 'made-for-kids')}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem id="youtube-not-made-for-kids" value="not-made-for-kids" />
                <label className="text-sm" htmlFor="youtube-not-made-for-kids">
                  {t('publishing.youtube.audience.notMadeForKids')}
                </label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem id="youtube-made-for-kids" value="made-for-kids" />
                <label className="text-sm" htmlFor="youtube-made-for-kids">
                  {t('publishing.youtube.audience.madeForKids')}
                </label>
              </div>
            </RadioGroup>
          </fieldset>
        </div>

        <p className="rounded-md bg-muted/60 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
          {t('publishing.youtube.visibilityCaveat')}
        </p>

        <PublishWhenControl
          idPrefix="youtube"
          when={when}
          onWhenChange={(next) => {
            setWhen(next);
            setWhenError(null);
            if (next === 'later' && !datetimeLocal) setDatetimeLocal(minDatetimeLocal);
          }}
          datetimeLocal={datetimeLocal}
          onDatetimeLocalChange={(value) => {
            setDatetimeLocal(value);
            setWhenError(null);
          }}
          minDatetimeLocal={minDatetimeLocal}
          error={whenError}
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={close} disabled={submitting}>
            {t('publishing.youtube.cancel')}
          </Button>
          <Button
            data-testid="youtube-publish-submit"
            type="submit"
            disabled={!formIsValid || submitting}
          >
            {submitting && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
            {submitting ? t('publishing.youtube.publishing') : t('publishing.youtube.publish')}
          </Button>
        </DialogFooter>
      </form>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}>
        <DialogContent
          className="max-h-[90vh] overflow-y-auto sm:max-w-xl"
          data-testid="publish-youtube-dialog"
        >
          <DialogHeader>
            <DialogTitle>{t('publishing.youtube.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('publishing.youtube.dialogDescription')}</DialogDescription>
          </DialogHeader>
          {renderBody()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!errorMessage} onOpenChange={(nextOpen) => {
        if (!nextOpen) setErrorMessage(null);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('publishing.youtube.errorTitle')}</DialogTitle>
            <DialogDescription>{errorMessage}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" onClick={() => setErrorMessage(null)}>
              {t('publishing.youtube.errorDismiss')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PublishToYouTubeDialog;
